import React, { useMemo } from "react";
import { Chart } from "primereact/chart";
import type { ChartData, ChartOptions } from "chart.js";
import type { HistogramBucket } from "../../../../types/targetGroupAnalytics";
import {
  COCHARGE_DARK_GREEN,
  SURFACE_BORDER,
  TEXT_COLOR_SECONDARY,
} from "../../../../styles/theme_constants";

interface DistributionHistogramProps {
  title: string;
  buckets: HistogramBucket[];
  /** Y-axis label (count unit), e.g. "Owners", "Groups". */
  unit?: string;
  /** X-axis label describing the bucket dimension, e.g. "Number of groups". */
  xLabel?: string;
}

/**
 * Pre-bucketed histogram (FR-008). Self-contained bar chart (rather than the
 * shared ReusableBarChart, which truncates labels to 3 chars for month
 * abbreviations and would mangle bucket labels like "11-50").
 */
const DistributionHistogram: React.FC<DistributionHistogramProps> = ({ title, buckets, unit, xLabel }) => {
  const hasData = buckets.length > 0;

  const data = useMemo<ChartData<"bar">>(
    () => ({
      labels: buckets.map((b) => b.label),
      datasets: [
        {
          label: unit || "Count",
          data: buckets.map((b) => b.count),
          backgroundColor: COCHARGE_DARK_GREEN,
          borderRadius: 6,
          barThickness: 18,
        },
      ],
    }),
    [buckets, unit],
  );

  const options = useMemo<ChartOptions<"bar">>(
    () => ({
      animation: { duration: 0 },
      maintainAspectRatio: false,
      plugins: { legend: { display: false } },
      scales: {
        x: {
          ticks: { color: TEXT_COLOR_SECONDARY, font: { weight: "bold" } },
          grid: { display: false },
          title: xLabel
            ? { display: true, text: xLabel, color: TEXT_COLOR_SECONDARY, font: { size: 11 } }
            : { display: false },
        },
        y: {
          beginAtZero: true,
          ticks: { color: TEXT_COLOR_SECONDARY, precision: 0 },
          grid: { color: SURFACE_BORDER },
          title: unit
            ? { display: true, text: unit, color: TEXT_COLOR_SECONDARY, font: { size: 11 } }
            : { display: false },
        },
      },
    }),
    [unit, xLabel],
  );

  return (
    <div className="px-2 py-2" data-testid="distribution-histogram">
      <div className="flex align-items-center gap-2 mb-2">
        <span
          style={{
            width: 3,
            height: 16,
            backgroundColor: COCHARGE_DARK_GREEN,
            display: "inline-block",
            borderRadius: 2,
            flexShrink: 0,
          }}
        />
        <span className="font-semibold text-800 text-sm">{title}</span>
      </div>
      {hasData ? (
        <Chart type="bar" data={data} options={options} height="220px" className="w-full" />
      ) : (
        <p className="text-color-secondary text-sm m-0 py-4 text-center">No data.</p>
      )}
    </div>
  );
};

export default DistributionHistogram;
